// The xlsx's sheets of saved views and dimensions (design 02 8.3).
//
// A saved view is a sheet: its table as on screen (pivot.ts: a line per
// column dimension, then a line per row) under its name and what is fixed
// or summed. A view of 지출 or 수입 (sliced with [사람="…", 월="…",
// 분류="…"] or summed with sum(…, over=…), its dimensions summed but not
// averaged) is written as SUMIFS over the 거래 sheet's Excel table
// (tensor거래), so that it follows rows added in Excel: 월 as two date
// conditions (the month's first day, the next month's first day), 사람 and
// 분류 (its 대분류 column) as text, "(없음)" as an empty cell. Anything else
// is written as values, with the view's source in a note.
//
// 차원: each dimension in a column, its items under it (the order formulas
// use; a range 1..N as numbers).
import type { TensorDim, TensorViewConfig } from '../api'
import { customDims } from '../dims'
import { type PivotValue, type Source, axesOf, headerSpans, pivot, rowHeads } from '../pivot'
import { NONE } from '../views'
import type { WCell, WSheet, XValue } from '../xlsx/write'
import type { BuiltinData } from './builtins'

const TABLE = 'tensor거래'
const BOLD = { b: true }
const MONEY = { nf: '#,##0' }
/** Excel's own errors; anything else (#DIM!) is written as text */
const EXCEL_ERRORS = new Set(['#NULL!', '#DIV/0!', '#VALUE!', '#REF!', '#NAME?', '#NUM!', '#N/A', '#SPILL!', '#CALC!'])

/** A sheet name Excel takes from a view's name (characters it refuses as "_"). */
export function sheetNameOf(name: string): string {
  let n = name.replace(/[:\\/?*[\]\u0000-\u001f\u007f]/g, '_').trim()
  n = n.replace(/^'+|'+$/g, '').trim()
  n = [...n].slice(0, 31).join('').trim()
  return n === '' || n.toLowerCase() === 'history' ? '보기' : n
}

/** Splits at the commas outside "strings". */
function splitArgs(text: string): string[] {
  const out: string[] = []
  let cur = ''
  let quoted = false
  for (const ch of text) {
    if (ch === '"') quoted = !quoted
    if (ch === ',' && !quoted) {
      out.push(cur)
      cur = ''
    } else cur += ch
  }
  out.push(cur)
  return out
}

/** A source made of 지출 or 수입 alone: which, and the items it is sliced to. */
export interface LedgerSource {
  kind: '지출' | '수입'
  where: Record<string, string>
}

/**
 * What a source is when Excel can sum it from the 거래 table: 지출, 수입,
 * either sliced (지출[분류="식비", 월="2026-10"]) or summed over a dimension
 * (sum(지출, over=사람)); null for anything else.
 */
export function ledgerSource(source: string): LedgerSource | null {
  const s = source.trim().replace(/^=\s*/, '')
  const sum = /^sum\(\s*(.+?)\s*,\s*over\s*=\s*(사람|월|분류)\s*\)$/i.exec(s)
  if (sum) return ledgerSource(sum[1]!)
  const m = /^(지출|수입)\s*(?:\[(.*)\])?$/s.exec(s)
  if (!m) return null
  const where: Record<string, string> = {}
  if (m[2] !== undefined) {
    for (const part of splitArgs(m[2])) {
      const c = /^\s*(사람|월|분류)\s*=\s*"((?:[^"]|"")*)"\s*$/.exec(part)
      if (!c || where[c[1]!] !== undefined) return null
      where[c[1]!] = c[2]!.replace(/""/g, '"')
    }
  }
  return { kind: m[1] as LedgerSource['kind'], where }
}

/** A SUMIFS criterion that is the item itself (no wildcards, no comparison). */
function criterion(item: string): string {
  if (item === NONE) return '""'
  let t = item.replace(/[~*?]/g, (c) => `~${c}`)
  if (/^[<>=]/.test(t)) t = `=${t}`
  return `"${t.replace(/"/g, '""')}"`
}

/** The SUMIFS of 지출 or 수입 at these items (without "="), or null. */
export function sumifsFormula(kind: LedgerSource['kind'], at: Record<string, string>): string | null {
  const parts = [`${TABLE}[금액]`, `${TABLE}[구분]`, `"${kind}"`]
  for (const dim of Object.keys(at)) if (!['사람', '월', '분류'].includes(dim)) return null
  if (at.사람 !== undefined) parts.push(`${TABLE}[사람]`, criterion(at.사람))
  if (at.분류 !== undefined) parts.push(`${TABLE}[대분류]`, criterion(at.분류))
  if (at.월 !== undefined) {
    const m = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(at.월)
    if (!m) return null
    const y = Number(m[1])
    const mo = Number(m[2])
    const [ny, nm] = mo === 12 ? [y + 1, 1] : [y, mo + 1]
    parts.push(`${TABLE}[날짜]`, `">="&DATE(${y},${mo},1)`, `${TABLE}[날짜]`, `"<"&DATE(${ny},${nm},1)`)
  }
  return `SUMIFS(${parts.join(',')})`
}

function xv(v: PivotValue): XValue {
  if (typeof v === 'string' && EXCEL_ERRORS.has(v)) return { error: v }
  return v
}

/** A saved view, with the engine's answer for its source (or the error). */
export interface ViewExport {
  name: string
  config: TensorViewConfig
  src: Source | null
  error?: string
}

/** The sheet of a saved view (its name is the view's, made fit: sheetNameOf). */
export function viewSheet(v: ViewExport, thisMonth: string): WSheet {
  const name = sheetNameOf(v.name)
  const title: WCell = { row: 0, col: 0, value: v.name, style: BOLD, note: `tensor 보기의 원본: =${v.config.source}` }
  if (!v.src) return { name, cells: [title, { row: 1, col: 0, value: `원본을 계산할 수 없습니다: ${v.error ?? '#CALC!'}` }] }
  const axes = axesOf(v.src, v.config, thisMonth)
  const p = pivot(v.src, axes)
  const cells: WCell[] = [title]
  let row = 1
  const fixed = axes.filter((a) => a.role === 'fix')
  const summed = axes.filter((a) => a.role === 'sum' || a.role === 'mean')
  if (fixed.length + summed.length > 0) {
    const what = [
      ...fixed.map((a) => `${a.name} = ${a.item ?? ''}`),
      ...summed.map((a) => `${a.name}: ${a.role === 'mean' ? '평균' : '합'}`),
    ]
    cells.push({ row: row++, col: 0, value: what.join(' · ') })
  }
  row++
  if (p.tooBig) {
    cells.push({ row, col: 0, value: '칸이 너무 많아 내보내지 않았습니다. 차원을 더 고정하거나 합쳐 주세요.' })
    return { name, cells }
  }
  const ledger = summed.some((a) => a.role === 'mean') ? null : ledgerSource(v.config.source)
  if (!ledger) title.note = `tensor 보기의 원본: =${v.config.source}\n엑셀 수식으로 바꿀 수 없어 값으로 내보냈습니다.`
  const lead = Math.max(1, p.rowDims.length)
  const top = row
  // the column headers: a line per column dimension
  if (p.colDims.length === 0) {
    p.rowDims.forEach((d, i) => cells.push({ row, col: i, value: d, style: BOLD }))
    cells.push({ row: row++, col: lead, value: '값', style: BOLD })
  }
  p.colDims.forEach((dim, level) => {
    if (level === p.colDims.length - 1 && p.rowDims.length)
      p.rowDims.forEach((d, i) => cells.push({ row, col: i, value: i === lead - 1 ? `${d} \\ ${dim}` : d, style: BOLD }))
    else cells.push({ row, col: lead - 1, value: dim, style: BOLD })
    for (const s of headerSpans(p.cols, level)) cells.push({ row, col: lead + s.at, value: s.label, style: BOLD })
    row++
  })
  const heads = rowHeads(p.rows)
  const at = (r: number, c: number): Record<string, string> => {
    const out: Record<string, string> = { ...(ledger?.where ?? {}) }
    p.rowDims.forEach((d, k) => (out[d] = p.rows[r]![k]!))
    p.colDims.forEach((d, k) => (out[d] = p.cols[c]![k]!))
    for (const a of fixed) if (a.item !== undefined) out[a.name] = a.item
    return out
  }
  p.rows.forEach((_, i) => {
    heads[i]!.forEach((h, k) => {
      if (h !== '') cells.push({ row, col: k, value: h })
    })
    p.cols.forEach((_, j) => {
      const v0 = p.cells[i]![j] ?? null
      const formula = ledger ? sumifsFormula(ledger.kind, at(i, j)) : null
      if (formula) cells.push({ row, col: lead + j, formula, value: xv(v0), style: MONEY })
      else if (v0 !== null) cells.push({ row, col: lead + j, value: xv(v0), ...(typeof v0 === 'number' ? { style: MONEY } : {}) })
    })
    row++
  })
  const widths: Record<string, number> = {}
  for (let k = 0; k < lead; k++) widths[String(k)] = 110
  return { name, cells, colWidths: widths, frozenRows: row > top ? top + Math.max(1, p.colDims.length) : 0, frozenCols: lead }
}

/** The 차원 sheet: a column each, its items under its name. */
export function dimsSheet(data: BuiltinData, dims: Record<number, TensorDim> | undefined): WSheet {
  const cols: { name: string; items: (string | number)[]; note: string }[] = [
    { name: '사람', items: data.dims.사람, note: '범주 차원' },
    { name: '월', items: data.dims.월, note: '순서 차원: 첫 달부터 마지막 달까지 빈 달 없이' },
    { name: '분류', items: data.dims.분류, note: '범주 차원: 대분류 (거래 시트의 대분류 열)' },
    { name: '결제수단', items: data.dims.결제수단, note: '범주 차원' },
  ]
  for (const d of customDims(dims)) {
    const items = data.custom.find((c) => c.name === d.name)?.items ?? []
    cols.push({
      name: d.name,
      items: d.size !== null ? items.map(Number) : items,
      note: d.kind === 'ordered' ? (d.size !== null ? `순서 차원: 1..${d.size}` : '순서 차원') : '범주 차원',
    })
  }
  const cells: WCell[] = []
  cols.forEach((c, j) => {
    cells.push({ row: 0, col: j, value: c.name, style: BOLD, note: c.note })
    c.items.forEach((it, i) => cells.push({ row: i + 1, col: j, value: it }))
  })
  return { name: '차원', cells, frozenRows: 1 }
}
